import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import Sidebar from "../components/Sidebar";
import Header from "../components/Header";

import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { API_BASE } from "../config/api";

function ChampionsDetail() {
    const { id } = useParams();

    const [desafio, setDesafio] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchDesafio = async () => {
        try {
            setLoading(true);

            const res = await fetch(`${API_BASE}/api/admin/champions/desafios/${id}`, {
                method: "GET",
                credentials: "include",
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.message || "No se pudo consultar el desafío.");
            }

            setDesafio(data.data);
        } catch (error) {
            console.error("Error fetchDesafio:", error);
            setDesafio(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDesafio();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formatDate = (value) => {
        if (!value) return "-";

        return new Date(value).toLocaleDateString("es-MX", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const stripHtml = (html) => {
        if (!html) return "-";

        const div = document.createElement("div");
        div.innerHTML = html;

        return div.textContent || div.innerText || "-";
    };

    const imageUrl =
        desafio?.imagenMiniatura ||
        desafio?.imagenSecundaria ||
        "https://placehold.co/800x500?text=Champions+Desafio";

    // Carga de SKU
    const [skus, setSkus] = useState([]);
    const [loadingSkus, setLoadingSkus] = useState(false);

    const [selectedSkuFile, setSelectedSkuFile] = useState(null);
    const [skuPreview, setSkuPreview] = useState(null);
    const [skuImportResult, setSkuImportResult] = useState(null);
    const [skuImportError, setSkuImportError] = useState("");
    const [skuImportLoading, setSkuImportLoading] = useState(false);

    const SKU_REQUIRED_COLUMNS = {
        desafio: ["desafioalquepertenece", "desafio", "desafío"],
        sku: ["sku", "codigo", "código", "numerodeparte", "númerodeparte", "noparte", "partnumber"],
        detalle: ["detalleproducto", "detalle", "descripcion", "descripción", "producto"],
    };

    const normalizeHeader = (value) => {
        return String(value || "")
            .trim()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, "")
            .replace(/_/g, "")
            .replace(/-/g, "");
    };

    const truncateText = (text, maxLength = 30) => {
        if (!text) return "-";

        const value = String(text).trim();

        if (value.length <= maxLength) return value;

        return `${value.substring(0, maxLength)}...`;
    };

    const normalizeSku = (value) => {
        return String(value || "")
            .trim()
            .toUpperCase()
            .replace(/\s+/g, "");
    };

    const findColumn = (headers, aliases) => {
        return headers.find((header) => aliases.includes(normalizeHeader(header))) || null;
    };

    const openModal = (modalId) => {
        const modalEl = document.getElementById(modalId);

        if (window.bootstrap && modalEl) {
            const modal =
                window.bootstrap.Modal.getInstance(modalEl) ||
                new window.bootstrap.Modal(modalEl);

            modal.show();
        }
    };

    const closeModal = (modalId) => {
        const modalEl = document.getElementById(modalId);

        if (window.bootstrap && modalEl) {
            const modal =
                window.bootstrap.Modal.getInstance(modalEl) ||
                new window.bootstrap.Modal(modalEl);

            modal.hide();
        }
    };

    const resetSkuImportModal = () => {
        setSelectedSkuFile(null);
        setSkuPreview(null);
        setSkuImportResult(null);
        setSkuImportError("");
        setSkuImportLoading(false);
    };

    const handleOpenSkuImportModal = () => {
        resetSkuImportModal();
        openModal("importSkusModal");
    };

    const fetchSkus = async () => {
        try {
            setLoadingSkus(true);

            const res = await fetch(
                `${API_BASE}/api/admin/champions/desafios/${id}/skus?limit=100`,
                {
                    method: "GET",
                    credentials: "include",
                }
            );

            const data = await res.json();

            if (!res.ok || !data.success) {
                throw new Error(data.message || "No se pudieron consultar los SKUs.");
            }

            setSkus(data.data?.items || []);
        } catch (error) {
            console.error("Error fetchSkus:", error);
            setSkus([]);
        } finally {
            setLoadingSkus(false);
        }
    };

    useEffect(() => {
        fetchDesafio();
        fetchSkus();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleSkuFileChange = (e) => {
        const file = e.target.files?.[0] || null;

        setSelectedSkuFile(file);
        setSkuPreview(null);
        setSkuImportResult(null);
        setSkuImportError("");
    };

    const handlePreviewSkuExcel = async () => {
    try {
        setSkuImportError("");
        setSkuImportResult(null);

        if (!selectedSkuFile) {
            setSkuImportError("Selecciona un archivo Excel.");
            return;
        }

        const extension = selectedSkuFile.name.split(".").pop()?.toLowerCase();

        if (!["xlsx", "xls"].includes(extension)) {
            setSkuImportError("Solo se permiten archivos .xlsx o .xls.");
            return;
        }

        const buffer = await selectedSkuFile.arrayBuffer();

        const workbook = XLSX.read(buffer, {
            type: "array",
            cellDates: false,
            raw: false,
        });

        const sheetName = workbook.SheetNames?.[0];

        if (!sheetName) {
            setSkuImportError("El archivo no contiene hojas.");
            return;
        }

        const worksheet = workbook.Sheets[sheetName];

        const rows = XLSX.utils.sheet_to_json(worksheet, {
            defval: "",
            raw: false,
        });

        if (!rows.length) {
            setSkuImportError("El archivo no contiene registros.");
            return;
        }

        const headers = Object.keys(rows[0] || {});

        const desafioColumn = findColumn(headers, SKU_REQUIRED_COLUMNS.desafio);
        const skuColumn = findColumn(headers, SKU_REQUIRED_COLUMNS.sku);
        const detalleColumn = findColumn(headers, SKU_REQUIRED_COLUMNS.detalle);

        if (!skuColumn) {
            setSkuImportError(
                "No se encontró la columna SKU. El Excel debe contener al menos la columna SKU."
            );
            return;
        }

        const seenSkus = new Set();

        const existingSkuMap = new Map(
            skus.map((sku) => [
                normalizeSku(sku.skuCodigoNormalizado || sku.skuCodigo),
                sku,
            ])
        );

        let validRows = 0;
        let errorRows = 0;
        let duplicatedRows = 0;
        let emptySkuRows = 0;
        let crearRows = 0;
        let actualizarRows = 0;

        const previewRows = rows.map((row, index) => {
            const desafioValue = desafioColumn
                ? String(row[desafioColumn] || "").trim()
                : "";

            const skuOriginal = String(row[skuColumn] || "").trim();
            const skuNormalizado = normalizeSku(skuOriginal);

            const detalleProducto = detalleColumn
                ? String(row[detalleColumn] || "").trim()
                : "";

            const errors = [];

            let estado = "Válida";
            let accion = "Crear";

            if (!skuNormalizado) {
                errors.push("SKU requerido.");
                emptySkuRows += 1;
            }

            if (skuNormalizado && seenSkus.has(skuNormalizado)) {
                errors.push("SKU duplicado en archivo.");
                duplicatedRows += 1;
            }

            if (skuNormalizado && !seenSkus.has(skuNormalizado)) {
                seenSkus.add(skuNormalizado);
            }

            if (errors.length) {
                estado = "Error";
                accion = "Error";
                errorRows += 1;
            } else {
                const existingSku = existingSkuMap.get(skuNormalizado);

                if (existingSku) {
                    accion = "Actualizar";
                    actualizarRows += 1;
                } else {
                    accion = "Crear";
                    crearRows += 1;
                }

                validRows += 1;
            }

            return {
                rowNumber: index + 2,
                desafio: desafioValue,
                sku: skuOriginal,
                skuNormalizado,
                detalleProducto,
                estado,
                accion,
                status: errors.length ? "error" : "valido",
                errors,
            };
        });

        setSkuPreview({
            totalRows: rows.length,
            validRows,
            errorRows,
            crearRows,
            actualizarRows,
            duplicatedRows,
            emptySkuRows,
            uniqueSkus: seenSkus.size,
            columns: {
                desafioColumn,
                skuColumn,
                detalleColumn,
            },
            rows: previewRows,
        });
    } catch (error) {
        console.error("Error handlePreviewSkuExcel:", error);
        setSkuImportError(error.message || "No se pudo validar el Excel.");
    }
};

    const handleImportSkus = async () => {
    try {
        setSkuImportError("");
        setSkuImportResult(null);

        if (!selectedSkuFile) {
            setSkuImportError("Selecciona un archivo Excel.");
            return;
        }

        if (!skuPreview) {
            setSkuImportError("Primero valida el archivo antes de importar.");
            return;
        }

        if (skuPreview.validRows <= 0) {
            setSkuImportError("No hay registros válidos para importar.");
            return;
        }

        setSkuImportLoading(true);

        const formData = new FormData();
        formData.append("archivo", selectedSkuFile);

        const res = await fetch(
            `${API_BASE}/api/admin/champions/desafios/${id}/skus/import`,
            {
                method: "POST",
                credentials: "include",
                body: formData,
            }
        );

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudieron importar los SKUs.");
        }

        setSkuImportResult(data.data);

        await fetchSkus();
        await fetchDesafio();

        Swal.fire({
            icon: "success",
            title: "Importación completa",
            text: "Los SKUs fueron procesados correctamente.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
    } catch (error) {
        console.error("Error handleImportSkus:", error);
        setSkuImportError(error.message || "No se pudieron importar los SKUs.");
    } finally {
        setSkuImportLoading(false);
    }
};

// CRUD SKUS

const INITIAL_SKU_FORM = {
    skuCodigo: "",
    detalleProducto: "",
    activo: "1",
};

const [skuForm, setSkuForm] = useState(INITIAL_SKU_FORM);
const [skuFormMode, setSkuFormMode] = useState("create"); // create | edit
const [selectedSku, setSelectedSku] = useState(null);
const [savingSku, setSavingSku] = useState(false);

// const openModal = (modalId) => {
//     const modalEl = document.getElementById(modalId);

//     if (window.bootstrap && modalEl) {
//         const modal =
//             window.bootstrap.Modal.getInstance(modalEl) ||
//             new window.bootstrap.Modal(modalEl);

//         modal.show();
//     }
// };

// const closeModal = (modalId) => {
//     const modalEl = document.getElementById(modalId);

//     if (window.bootstrap && modalEl) {
//         const modal =
//             window.bootstrap.Modal.getInstance(modalEl) ||
//             new window.bootstrap.Modal(modalEl);

//         modal.hide();
//     }
// };

const resetSkuForm = () => {
    setSkuForm(INITIAL_SKU_FORM);
    setSkuFormMode("create");
    setSelectedSku(null);
};

const handleSkuFormChange = (field, value) => {
    setSkuForm((prev) => ({
        ...prev,
        [field]: value,
    }));
};

const openCreateSkuModal = () => {
    resetSkuForm();
    setSkuFormMode("create");
    openModal("skuCrudModal");
};

const openEditSkuModal = (sku) => {
    setSelectedSku(sku);
    setSkuFormMode("edit");

    setSkuForm({
        skuCodigo: sku.skuCodigo || "",
        detalleProducto: sku.detalleProducto || "",
        activo: String(sku.activo ?? 1),
    });

    openModal("skuCrudModal");
};

const validateSkuForm = () => {
    if (!skuForm.skuCodigo.trim()) {
        Swal.fire({
            icon: "error",
            title: "Campo requerido",
            text: "El SKU es requerido.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
        return false;
    }

    if (skuForm.skuCodigo.trim().length > 120) {
        Swal.fire({
            icon: "error",
            title: "SKU inválido",
            text: "El SKU no puede exceder 120 caracteres.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
        return false;
    }

    if (skuForm.detalleProducto.trim().length > 255) {
        Swal.fire({
            icon: "error",
            title: "Detalle inválido",
            text: "El detalle del producto no puede exceder 255 caracteres.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
        return false;
    }

    return true;
};

const handleSaveSku = async (e) => {
    e.preventDefault();

    if (!validateSkuForm()) return;

    try {
        setSavingSku(true);

        const payload = {
            skuCodigo: skuForm.skuCodigo.trim(),
            detalleProducto: skuForm.detalleProducto.trim() || null,
            activo: Number(skuForm.activo),
        };

        const isEdit = skuFormMode === "edit" && selectedSku?.desafioSkuId;

        const url = isEdit
            ? `${API_BASE}/api/admin/champions/desafios/${id}/skus/${selectedSku.desafioSkuId}`
            : `${API_BASE}/api/admin/champions/desafios/${id}/skus`;

        const method = isEdit ? "PUT" : "POST";

        const res = await fetch(url, {
            method,
            credentials: "include",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudo guardar el SKU.");
        }

        closeModal("skuCrudModal");
        resetSkuForm();

        await fetchSkus();

        Swal.fire({
            icon: "success",
            title: "Correcto",
            text: isEdit
                ? "SKU actualizado correctamente."
                : "SKU agregado correctamente.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
    } catch (error) {
        console.error("Error handleSaveSku:", error);

        Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message || "No se pudo guardar el SKU.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
    } finally {
        setSavingSku(false);
    }
};

const handleDeleteSku = async (sku) => {
    const result = await Swal.fire({
        icon: "warning",
        title: "¿Desactivar SKU?",
        text: `El SKU "${sku.skuCodigo}" se desactivará del desafío. No se eliminará físicamente.`,
        showCancelButton: true,
        confirmButtonText: "Sí, desactivar",
        cancelButtonText: "Cancelar",
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
    });

    if (!result.isConfirmed) return;

    try {
        const res = await fetch(
            `${API_BASE}/api/admin/champions/desafios/${id}/skus/${sku.desafioSkuId}`,
            {
                method: "DELETE",
                credentials: "include",
            }
        );

        const data = await res.json();

        if (!res.ok || !data.success) {
            throw new Error(data.message || "No se pudo desactivar el SKU.");
        }

        await fetchSkus();

        Swal.fire({
            icon: "success",
            title: "SKU desactivado",
            text: "El SKU fue desactivado correctamente.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
    } catch (error) {
        console.error("Error handleDeleteSku:", error);

        Swal.fire({
            icon: "error",
            title: "Error",
            text: error.message || "No se pudo desactivar el SKU.",
            confirmButtonText: "Aceptar",
            confirmButtonColor: "#193E92",
        });
    }
};

// TAB PARTICIPANTES
const desafioId = id;

const [participantes, setParticipantes] = useState([]);
const [participantesLoading, setParticipantesLoading] = useState(false);
const [participantesError, setParticipantesError] = useState("");

const fetchParticipantes = async () => {
    if (!desafioId) return;

    try {
        setParticipantesLoading(true);
        setParticipantesError("");

        const response = await fetch(
            `${API_BASE}/api/admin/champions/participantes?desafioId=${desafioId}&limit=100`,
            {
                method: "GET",
                credentials: "include",
            }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
            throw new Error(
                result.message || "No se pudieron consultar los participantes."
            );
        }

        setParticipantes(result.data?.items || []);
    } catch (error) {
        console.error("Error al consultar participantes:", error);
        setParticipantesError(error.message);
        setParticipantes([]);
    } finally {
        setParticipantesLoading(false);
    }
};

useEffect(() => {
    fetchParticipantes();
}, [desafioId]);

const getRegistroBadgeClass = (estatus) => {
    switch (estatus) {
        case "INSCRITO":
            return "bg-primary-subtle text-primary";
        case "EN_PROGRESO":
            return "bg-warning-subtle text-warning";
        case "COMPLETADO":
            return "bg-success-subtle text-success";
        case "CANCELADO":
            return "bg-danger-subtle text-danger";
        default:
            return "bg-secondary";
    }
};

const formatRegistroEstatus = (estatus) => {
    switch (estatus) {
        case "INSCRITO":
            return "Inscrito";
        case "EN_PROGRESO":
            return "En progreso";
        case "COMPLETADO":
            return "Completado";
        case "CANCELADO":
            return "Cancelado";
        default:
            return estatus || "Sin estado";
    }
};


    return (
        <>
            <Sidebar />

            <main className="main-content">
                <Header />

                <div className="content-inner container-fluid pb-0" id="page_layout">
                    <div className="d-flex justify-content-between align-items-center flex-wrap mb-4 gap-3">
                        <div className="d-flex flex-column">
                            <h3>Detalle del Desafío Champions</h3>
                            <p className="text-primary mb-0">
                                Administración del desafío #{id}
                            </p>
                        </div>

                        <div>
                            <button
                                type="button"
                                className="btn btn-secondary-subtle btn-sm"
                                onClick={() => window.history.back()}
                            >
                                <span className="btn-inner">
                                        <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">                                <path opacity="0.4" d="M12 22.0002C6.485 22.0002 2 17.5142 2 12.0002C2 6.48624 6.485 2.00024 12 2.00024C17.514 2.00024 22 6.48624 22 12.0002C22 17.5142 17.514 22.0002 12 22.0002Z" fill="currentColor"></path>                                <path d="M13.4425 16.2208C13.2515 16.2208 13.0595 16.1478 12.9135 16.0018L9.42652 12.5318C9.28552 12.3908 9.20652 12.1998 9.20652 11.9998C9.20652 11.8008 9.28552 11.6098 9.42652 11.4688L12.9135 7.99683C13.2065 7.70483 13.6805 7.70483 13.9735 7.99883C14.2655 8.29283 14.2645 8.76783 13.9715 9.05983L11.0185 11.9998L13.9715 14.9398C14.2645 15.2318 14.2655 15.7058 13.9735 15.9998C13.8275 16.1478 13.6345 16.2208 13.4425 16.2208Z" fill="currentColor"></path>                                </svg>                            
                                </span>
                                {" "}Regresar
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div className="card">
                            <div className="card-body text-center py-5">
                                <div className="iq-loader-box">
                                    <div className="iq-loader-1"></div>
                                </div>
                                <p className="text-muted mt-3 mb-0">
                                    Cargando información del desafío...
                                </p>
                            </div>
                        </div>
                    ) : !desafio ? (
                        <div className="alert alert-danger">
                            No se pudo cargar la información del desafío.
                        </div>
                    ) : (
                        <div className="row">
                            {/* COLUMNA IZQUIERDA */}
                            <div className="col-lg-3 col-md-12 mb-4">
                                <div className="card">
                                    <div className="card-body">
                                        <div className="mb-3">
                                            <img
                                                src={imageUrl}
                                                alt={desafio.titulo}
                                                className="img-fluid w-100"
                                                style={{
                                                    borderRadius: "16px",
                                                    objectFit: "cover",
                                                    maxHeight: "260px",
                                                }}
                                            />
                                        </div>

                                        <div className="d-flex justify-content-between align-items-start mb-3">
                                            <div>
                                                <h4 className="mb-1">
                                                    {desafio.titulo}
                                                </h4>

                                                <p className="text-primary mb-0">
                                                    {desafio.productoTitulo || "-"}
                                                </p>
                                            </div>

                                            {Number(desafio.activo) === 1 ? (
                                                <span className="badge bg-success-subtle p-2 text-success">
                                                    Activo
                                                </span>
                                            ) : (
                                                <span className="badge bg-danger-subtle p-2 text-danger">
                                                    Inactivo
                                                </span>
                                            )}
                                        </div>

                                        {/* <div className="mb-4">
                                            <p className="text-muted mb-0">
                                                {stripHtml(desafio.descripcion)}
                                            </p>
                                        </div> */}

                                        <div className="border-top pt-3">
                                            {/* <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">ID Desafío</span>
                                                <strong>{desafio.desafioId}</strong>
                                            </div> */}

                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Niveles</span>
                                                <strong>{desafio.niveles?.length || 0}</strong>
                                            </div>

                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Tiene vigencia</span>
                                                <strong>
                                                    {Number(desafio.tieneVigencia) === 1
                                                        ? "Sí"
                                                        : "No"}
                                                </strong>
                                            </div>

                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Creado por</span>
                                                <strong>{desafio.creadoPor || "-"}</strong>
                                            </div>

                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Actualizado por</span>
                                                <strong>{desafio.actualizadoPor || "-"}</strong>
                                            </div>

                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted">Fecha registro</span>
                                                <strong>{formatDate(desafio.fechaRegistro)}</strong>
                                            </div>

                                            <div className="d-flex justify-content-between">
                                                <span className="text-muted">Última actualización</span>
                                                <strong>{formatDate(desafio.fechaActualizacion)}</strong>
                                            </div>
                                        </div>

                                        <div className="d-grid gap-2 mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-primary btn-sm"
                                            >
                                                Editar información
                                            </button>

                                            <button
                                                type="button"
                                                className="btn btn-outline-primary btn-sm"
                                            >
                                                Administrar niveles
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* COLUMNA DERECHA */}
                            <div className="col-lg-9 col-md-12 mb-4">
                                <div className="card">
                                    <div className="card-header pb-0">
                                        <ul
                                            className="nav nav-tabs"
                                            id="championsDetailTabs"
                                            role="tablist"
                                        >
                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className="nav-link active"
                                                    id="participantes-tab"
                                                    data-bs-toggle="tab"
                                                    data-bs-target="#participantes-tab-pane"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="participantes-tab-pane"
                                                    aria-selected="true"
                                                >
                                                    <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">                                <path d="M11.997 15.1746C7.684 15.1746 4 15.8546 4 18.5746C4 21.2956 7.661 21.9996 11.997 21.9996C16.31 21.9996 19.994 21.3206 19.994 18.5996C19.994 15.8786 16.334 15.1746 11.997 15.1746Z" fill="currentColor"></path>                                <path opacity="0.4" d="M11.9971 12.5838C14.9351 12.5838 17.2891 10.2288 17.2891 7.29176C17.2891 4.35476 14.9351 1.99976 11.9971 1.99976C9.06008 1.99976 6.70508 4.35476 6.70508 7.29176C6.70508 10.2288 9.06008 12.5838 11.9971 12.5838Z" fill="currentColor"></path>                                </svg>                            
                                                    {" "}Lista de participantes
                                                </button>
                                            </li>

                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className="nav-link"
                                                    id="skus-tab"
                                                    data-bs-toggle="tab"
                                                    data-bs-target="#skus-tab-pane"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="skus-tab-pane"
                                                    aria-selected="false"
                                                >
                                                    <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">                                <path opacity="0.4" fill-rule="evenodd" clip-rule="evenodd" d="M9.18824 3.74722C9.18824 3.33438 8.84724 3 8.42724 3H8.42624L6.79724 3.00098C4.60624 3.00294 2.82324 4.75331 2.82324 6.90279V8.76201C2.82324 9.17386 3.16424 9.50923 3.58424 9.50923C4.00424 9.50923 4.34624 9.17386 4.34624 8.76201V6.90279C4.34624 5.57604 5.44624 4.4964 6.79824 4.49444L8.42724 4.49345C8.84824 4.49345 9.18824 4.15907 9.18824 3.74722ZM17.1931 3.00029H15.6001C15.1801 3.00029 14.8391 3.33468 14.8391 3.74751C14.8391 4.15936 15.1801 4.49277 15.6001 4.49277H17.1931C18.5501 4.49277 19.6541 5.57535 19.6541 6.90603V8.7623C19.6541 9.17415 19.9951 9.50952 20.4151 9.50952C20.8361 9.50952 21.1761 9.17415 21.1761 8.7623V6.90603C21.1761 4.75165 19.3901 3.00029 17.1931 3.00029ZM9.23804 6.74266H14.762C15.367 6.74266 15.948 6.98094 16.371 7.40554C16.797 7.83407 17.033 8.40968 17.032 9.00883V10.2542C17.027 10.4003 16.908 10.5189 16.759 10.5229H7.23904C7.09104 10.518 6.97204 10.3993 6.96904 10.2542V9.00883C6.95804 7.76837 7.97404 6.75541 9.23804 6.74266Z" fill="currentColor"></path>                                <path d="M22.239 12.0413H1.762C1.342 12.0413 1 12.3756 1 12.7885C1 13.2003 1.342 13.5337 1.762 13.5337H2.823V17.0972C2.823 19.2467 4.607 20.9971 6.798 20.999L8.427 21C8.848 21 9.188 20.6656 9.189 20.2528C9.189 19.841 8.848 19.5066 8.428 19.5066L6.8 19.5056C5.447 19.5036 4.346 18.424 4.346 17.0972V13.5337H6.969V14.5251C6.959 15.7656 7.974 16.7795 9.238 16.7913H14.762C16.027 16.7795 17.042 15.7656 17.032 14.5251V13.5337H19.655V17.0933C19.655 18.425 18.551 19.5066 17.194 19.5066H15.601C15.18 19.5066 14.839 19.841 14.839 20.2528C14.839 20.6656 15.18 21 15.601 21H17.194C19.39 21 21.177 19.2487 21.177 17.0933V13.5337H22.239C22.659 13.5337 23 13.2003 23 12.7885C23 12.3756 22.659 12.0413 22.239 12.0413Z" fill="currentColor"></path>                                </svg>                            
                                                    {" "}Lista de SKUs
                                                </button>
                                            </li>

                                            <li className="nav-item" role="presentation">
                                                <button
                                                    className="nav-link"
                                                    id="reporte-tab"
                                                    data-bs-toggle="tab"
                                                    data-bs-target="#reporte-tab-pane"
                                                    type="button"
                                                    role="tab"
                                                    aria-controls="reporte-tab-pane"
                                                    aria-selected="false"
                                                >
                                                    <svg width="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">                                <path opacity="0.4" d="M16.6756 2H7.33333C3.92889 2 2 3.92889 2 7.33333V16.6667C2 20.0711 3.92889 22 7.33333 22H16.6756C20.08 22 22 20.0711 22 16.6667V7.33333C22 3.92889 20.08 2 16.6756 2Z" fill="currentColor"></path>                                <path d="M7.36866 9.3689C6.91533 9.3689 6.54199 9.74223 6.54199 10.2045V17.0756C6.54199 17.5289 6.91533 17.9022 7.36866 17.9022C7.83088 17.9022 8.20421 17.5289 8.20421 17.0756V10.2045C8.20421 9.74223 7.83088 9.3689 7.36866 9.3689Z" fill="currentColor"></path>                                <path d="M12.0352 6.08887C11.5818 6.08887 11.2085 6.4622 11.2085 6.92442V17.0755C11.2085 17.5289 11.5818 17.9022 12.0352 17.9022C12.4974 17.9022 12.8707 17.5289 12.8707 17.0755V6.92442C12.8707 6.4622 12.4974 6.08887 12.0352 6.08887Z" fill="currentColor"></path>                                <path d="M16.6398 12.9956C16.1775 12.9956 15.8042 13.3689 15.8042 13.8312V17.0756C15.8042 17.5289 16.1775 17.9023 16.6309 17.9023C17.0931 17.9023 17.4664 17.5289 17.4664 17.0756V13.8312C17.4664 13.3689 17.0931 12.9956 16.6398 12.9956Z" fill="currentColor"></path>                                </svg>                            
                                                    {" "}Generar reporte
                                                </button>
                                            </li>
                                        </ul>
                                    </div>

                                    <div className="card-body">
                                        <div className="tab-content" id="championsDetailTabsContent">
                                            {/* TAB PARTICIPANTES */}
                                            <div
                                                className="tab-pane fade show active"
                                                id="participantes-tab-pane"
                                                role="tabpanel"
                                                aria-labelledby="participantes-tab"
                                                tabIndex="0"
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div>
                                                        <h5 className="mb-0">
                                                            Lista de participantes
                                                        </h5>

                                                        <small className="text-muted">
                                                            Usuarios inscritos a este desafío Champions.
                                                        </small>
                                                    </div>

                                                    <button
                                                        type="button"
                                                        className="btn btn-outline-primary btn-sm"
                                                        onClick={fetchParticipantes}
                                                        disabled={participantesLoading}
                                                    >
                                                        {participantesLoading ? "Actualizando..." : "Actualizar"}
                                                    </button>
                                                </div>

                                                {participantesError ? (
                                                    <div className="alert alert-danger py-2">
                                                        {participantesError}
                                                    </div>
                                                ) : null}

                                                <div className="table-responsive">
                                                    <table className="table table-striped table-hover align-middle">
                                                        <thead>
                                                            <tr>
                                                                <th>Usuario</th>
                                                                <th>Correo</th>
                                                                <th>Disty</th>
                                                                <th>Progreso</th>
                                                                <th>Estado</th>
                                                                <th className="text-end">Acción</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {participantesLoading ? (
                                                                <tr>
                                                                    <td colSpan="6" className="text-center py-4">
                                                                        <span className="text-muted">
                                                                            Cargando participantes...
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ) : participantes.length === 0 ? (
                                                                <tr>
                                                                    <td colSpan="6" className="text-center py-4">
                                                                        <span className="text-muted">
                                                                            Sin participantes registrados.
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                participantes.map((participante) => {
                                                                    const porcentaje = Number(
                                                                        participante.porcentajeProgreso || 0
                                                                    );

                                                                    return (
                                                                        <tr key={participante.Registro_ID}>
                                                                            <td>
                                                                                <div className="fw-semibold">
                                                                                    {participante.Usuario_Nombre}{" "}
                                                                                    {participante.Usuario_Apellido}
                                                                                </div>

                                                                                <small className="text-muted">
                                                                                    {participante.Grado_Nombre || "Sin grado"} ·{" "}
                                                                                    {participante.Region_Nombre || "Sin región"}
                                                                                </small>
                                                                            </td>

                                                                            <td>
                                                                                <span className="text-muted">
                                                                                    {participante.Usuario_Correo}
                                                                                </span>
                                                                            </td>

                                                                            <td>
                                                                                {participante.Disty_Nombre || "Sin disty"}
                                                                            </td>

                                                                            <td style={{ minWidth: "180px" }}>
                                                                                <div className="d-flex justify-content-between mb-1">
                                                                                    <small className="text-muted">
                                                                                        {participante.nivelesCompletados || 0}/
                                                                                        {participante.totalNiveles || 0} niveles
                                                                                    </small>

                                                                                    <small className="fw-semibold">
                                                                                        {porcentaje}%
                                                                                    </small>
                                                                                </div>

                                                                                <div
                                                                                    className="progress"
                                                                                    style={{ height: "8px" }}
                                                                                >
                                                                                    <div
                                                                                        className="progress-bar"
                                                                                        role="progressbar"
                                                                                        style={{ width: `${porcentaje}%` }}
                                                                                        aria-valuenow={porcentaje}
                                                                                        aria-valuemin="0"
                                                                                        aria-valuemax="100"
                                                                                    />
                                                                                </div>

                                                                                <small className="text-muted d-block mt-1">
                                                                                    Evidencias:{" "}
                                                                                    {participante.totalEvidencias || 0}
                                                                                    {" · "}
                                                                                    Pendientes:{" "}
                                                                                    {participante.totalEvidenciasPendientes || 0}
                                                                                </small>
                                                                            </td>

                                                                            <td>
                                                                                <span
                                                                                    className={`badge p-2 ${getRegistroBadgeClass(
                                                                                        participante.Registro_Estatus
                                                                                    )}`}
                                                                                >
                                                                                    {formatRegistroEstatus(
                                                                                        participante.Registro_Estatus
                                                                                    )}
                                                                                </span>
                                                                            </td>

                                                                            <td className="text-end">
                                                                                <Link
                                                                                    to={`/champions/participantes/${participante.Registro_ID}`}
                                                                                    className="btn btn-icon btn-info-subtle ms-2"
                                                                                >
                                                                                    <span class="btn-inner"><svg class="icon-32" width="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.4" fill-rule="evenodd" clip-rule="evenodd" d="M17.7366 6.04606C19.4439 7.36388 20.8976 9.29455 21.9415 11.7091C22.0195 11.8924 22.0195 12.1067 21.9415 12.2812C19.8537 17.1103 16.1366 20 12 20H11.9902C7.86341 20 4.14634 17.1103 2.05854 12.2812C1.98049 12.1067 1.98049 11.8924 2.05854 11.7091C4.14634 6.87903 7.86341 4 11.9902 4H12C14.0683 4 16.0293 4.71758 17.7366 6.04606ZM8.09756 12C8.09756 14.1333 9.8439 15.8691 12 15.8691C14.1463 15.8691 15.8927 14.1333 15.8927 12C15.8927 9.85697 14.1463 8.12121 12 8.12121C9.8439 8.12121 8.09756 9.85697 8.09756 12Z" fill="currentColor"></path><path d="M14.4308 11.997C14.4308 13.3255 13.3381 14.4115 12.0015 14.4115C10.6552 14.4115 9.5625 13.3255 9.5625 11.997C9.5625 11.8321 9.58201 11.678 9.61128 11.5228H9.66006C10.743 11.5228 11.621 10.6695 11.6601 9.60184C11.7674 9.58342 11.8845 9.57275 12.0015 9.57275C13.3381 9.57275 14.4308 10.6588 14.4308 11.997Z" fill="currentColor"></path></svg></span>
                                                                                </Link>
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                })
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>

                                            {/* TAB SKUS */}
                                            <div
                                                className="tab-pane fade"
                                                id="skus-tab-pane"
                                                role="tabpanel"
                                                aria-labelledby="skus-tab"
                                                tabIndex="0"
                                            >
                                                <div className="mb-4">
                                                    <h5 className="mb-0">SKUs permitidos</h5>
                                                    <small className="text-muted">
                                                        Aquí podrás importar y consultar los números de parte válidos para este desafío.
                                                    </small>
                                                </div>
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    

                                                    <div className="col-md-4">
                                                        <label class="form-label mb-1">Buscar</label>
                                                        <input class="form-control form-control-sm" placeholder="Buscar por nombre" type="text" value=""></input>
                                                    </div>

                                                    <div className="col-md-4 d-flex justify-content-end gap-2 ms-auto">

                                                        <button type="button" class="btn btn-secondary-subtle btn-sm" title="Limpiar búsqueda">
                                                            <svg width="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g opacity="0.4"><path d="M4.88076 14.6713C4.74978 14.2784 4.32504 14.066 3.93208 14.197C3.53912 14.328 3.32675 14.7527 3.45774 15.1457L4.88076 14.6713ZM20.8808 15.1457C21.0117 14.7527 20.7994 14.328 20.4064 14.197C20.0135 14.066 19.5887 14.2784 19.4577 14.6713L20.8808 15.1457ZM4.16925 14.9085C3.45774 15.1457 3.45785 15.146 3.45797 15.1464C3.45802 15.1465 3.45815 15.1469 3.45825 15.1472C3.45845 15.1478 3.45868 15.1485 3.45895 15.1493C3.45948 15.1509 3.46013 15.1528 3.46092 15.1551C3.46249 15.1597 3.46456 15.1657 3.46716 15.1731C3.47235 15.188 3.47961 15.2084 3.48902 15.2341C3.50782 15.2854 3.53521 15.3576 3.5717 15.4477C3.64461 15.6279 3.7542 15.8805 3.90468 16.1814C4.2048 16.7817 4.67223 17.5836 5.34308 18.3886C6.68942 20.0043 8.88343 21.6585 12.1693 21.6585V20.1585C9.45507 20.1585 7.64908 18.8128 6.49542 17.4284C5.91627 16.7334 5.5087 16.0354 5.24632 15.5106C5.11555 15.2491 5.02201 15.0329 4.96212 14.8849C4.9322 14.811 4.91076 14.7543 4.89733 14.7177C4.89062 14.6994 4.88593 14.6861 4.88318 14.6783C4.88181 14.6744 4.88093 14.6718 4.88053 14.6706C4.88033 14.67 4.88025 14.6698 4.88029 14.6699C4.88031 14.67 4.88036 14.6701 4.88044 14.6704C4.88047 14.6705 4.88056 14.6707 4.88058 14.6708C4.88067 14.671 4.88076 14.6713 4.16925 14.9085ZM12.1693 21.6585C15.4551 21.6585 17.6491 20.0043 18.9954 18.3886C19.6663 17.5836 20.1337 16.7817 20.4338 16.1814C20.5843 15.8805 20.6939 15.6279 20.7668 15.4477C20.8033 15.3576 20.8307 15.2854 20.8495 15.2341C20.8589 15.2084 20.8662 15.188 20.8713 15.1731C20.8739 15.1657 20.876 15.1597 20.8776 15.1551C20.8784 15.1528 20.879 15.1509 20.8796 15.1493C20.8798 15.1485 20.8801 15.1478 20.8803 15.1472C20.8804 15.1469 20.8805 15.1465 20.8805 15.1464C20.8807 15.146 20.8808 15.1457 20.1693 14.9085C19.4577 14.6713 19.4578 14.671 19.4579 14.6708C19.4579 14.6707 19.458 14.6705 19.4581 14.6704C19.4581 14.6701 19.4582 14.67 19.4582 14.6699C19.4583 14.6698 19.4582 14.67 19.458 14.6706C19.4576 14.6718 19.4567 14.6744 19.4553 14.6783C19.4526 14.6861 19.4479 14.6994 19.4412 14.7177C19.4277 14.7543 19.4063 14.811 19.3764 14.8849C19.3165 15.0329 19.223 15.2491 19.0922 15.5106C18.8298 16.0354 18.4222 16.7334 17.8431 17.4284C16.6894 18.8128 14.8834 20.1585 12.1693 20.1585V21.6585Z" fill="currentColor"></path><path d="M21.5183 19.2271C21.4293 19.2234 21.3427 19.196 21.2671 19.1465L16.3546 15.8924C16.2197 15.8026 16.1413 15.6537 16.148 15.4969C16.1546 15.34 16.2452 15.1982 16.3873 15.1202L21.5571 12.2926C21.7075 12.2106 21.8932 12.213 22.0416 12.3003C22.1907 12.387 22.2783 12.5436 22.2712 12.7096L22.014 18.7913C22.007 18.9573 21.9065 19.1059 21.7506 19.1797C21.6772 19.215 21.597 19.2305 21.5183 19.2271" fill="currentColor"></path></g><path d="M20.0742 10.0265C20.1886 10.4246 20.6041 10.6546 21.0022 10.5401C21.4003 10.4257 21.6302 10.0102 21.5158 9.61214L20.0742 10.0265ZM4.10803 8.88317C3.96071 9.27031 4.15513 9.70356 4.54226 9.85087C4.92939 9.99818 5.36265 9.80377 5.50996 9.41664L4.10803 8.88317ZM20.795 9.81934C21.5158 9.61214 21.5157 9.6118 21.5156 9.61144C21.5155 9.61129 21.5154 9.6109 21.5153 9.61059C21.5152 9.60998 21.515 9.60928 21.5147 9.60848C21.5143 9.60689 21.5137 9.60493 21.513 9.6026C21.5116 9.59795 21.5098 9.59184 21.5075 9.58431C21.503 9.56925 21.4966 9.54853 21.4882 9.52251C21.4716 9.47048 21.4473 9.39719 21.4146 9.3056C21.3493 9.12256 21.2503 8.8656 21.1126 8.55861C20.8378 7.94634 20.4044 7.12552 19.7678 6.29313C18.4902 4.62261 16.3673 2.87801 13.0844 2.74053L13.0216 4.23922C15.7334 4.35278 17.4816 5.77291 18.5763 7.20436C19.1258 7.92295 19.5038 8.63743 19.744 9.17271C19.8638 9.43949 19.9482 9.65937 20.0018 9.80972C20.0286 9.88483 20.0477 9.94238 20.0596 9.97951C20.0655 9.99808 20.0696 10.0115 20.072 10.0195C20.0732 10.0235 20.074 10.0261 20.0744 10.0273C20.0746 10.0278 20.0746 10.0281 20.0746 10.028C20.0746 10.0279 20.0745 10.0278 20.0745 10.0275C20.0744 10.0274 20.0744 10.0272 20.0743 10.0271C20.0743 10.0268 20.0742 10.0265 20.795 9.81934ZM13.0844 2.74053C9.80146 2.60306 7.54016 4.16407 6.12741 5.72193C5.42345 6.49818 4.92288 7.27989 4.59791 7.86704C4.43497 8.16144 4.31491 8.40923 4.23452 8.58617C4.1943 8.67471 4.16391 8.7457 4.14298 8.79616C4.13251 8.82139 4.1244 8.84151 4.11859 8.85613C4.11568 8.86344 4.11336 8.86938 4.1116 8.8739C4.11072 8.87616 4.10998 8.87807 4.10939 8.87962C4.10909 8.88039 4.10883 8.88108 4.1086 8.88167C4.10849 8.88196 4.10834 8.88234 4.10829 8.88249C4.10815 8.88284 4.10803 8.88317 4.80899 9.14991C5.50996 9.41664 5.50985 9.41692 5.50975 9.41719C5.50973 9.41725 5.50964 9.41749 5.50959 9.4176C5.5095 9.41784 5.50945 9.41798 5.50942 9.41804C5.50938 9.41816 5.50947 9.41792 5.50969 9.41734C5.51014 9.41619 5.51113 9.41365 5.51267 9.40979C5.51574 9.40206 5.52099 9.38901 5.52846 9.37101C5.5434 9.335 5.56719 9.27924 5.60018 9.20664C5.66621 9.0613 5.76871 8.84925 5.91031 8.59341C6.19442 8.08008 6.63084 7.39971 7.23855 6.72958C8.44912 5.39466 10.3098 4.12566 13.0216 4.23922L13.0844 2.74053Z" fill="currentColor"></path><path d="M8.78337 9.33604C8.72981 9.40713 8.65805 9.46292 8.57443 9.49703L3.1072 11.6951C2.95672 11.7552 2.78966 11.7352 2.66427 11.6407C2.53887 11.5462 2.47359 11.3912 2.48993 11.2299L3.09576 5.36863C3.11367 5.19823 3.22102 5.04666 3.37711 4.97402C3.5331 4.9005 3.71173 4.91728 3.84442 5.01726L8.70581 8.68052C8.8385 8.78051 8.90387 8.94759 8.8762 9.1178C8.86358 9.19825 8.83082 9.27308 8.78337 9.33604" fill="currentColor"></path></svg>
                                                        </button>

                                                        <button class="btn btn-primary btn-sm" onClick={openCreateSkuModal}>
                                                            <span class="btn-inner mr-5">
                                                                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon-24"><path d="M12 4V20M20 12H4" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"></path></svg>
                                                            </span>
                                                            Agregar Sku
                                                        </button>

                                                        <button
                                                            type="button"
                                                            className="btn btn-success btn-sm"
                                                            onClick={handleOpenSkuImportModal}
                                                        >
                                                            <svg width="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path opacity="0.4" d="M16.6667 2H7.33333C3.92889 2 2 3.92889 2 7.33333V16.6667C2 20.0622 3.92 22 7.33333 22H16.6667C20.0711 22 22 20.0622 22 16.6667V7.33333C22 3.92889 20.0711 2 16.6667 2Z" fill="currentColor"></path><path d="M15.3205 12.7083H12.7495V15.257C12.7495 15.6673 12.4139 16 12 16C11.5861 16 11.2505 15.6673 11.2505 15.257V12.7083H8.67955C8.29342 12.6687 8 12.3461 8 11.9613C8 11.5765 8.29342 11.2539 8.67955 11.2143H11.2424V8.67365C11.2824 8.29088 11.6078 8 11.996 8C12.3842 8 12.7095 8.29088 12.7495 8.67365V11.2143H15.3205C15.7066 11.2539 16 11.5765 16 11.9613C16 12.3461 15.7066 12.6687 15.3205 12.7083Z" fill="currentColor"></path></svg>
                                                            {" "}Importar SKUs
                                                        </button>
                                                    </div>
                                                </div>

                                                <div className="table-responsive">
                                                    <div
                                                        className="table-responsive"
                                                        style={{
                                                            maxHeight: "720px",
                                                            overflowY: "auto",
                                                            overflowX: "auto",
                                                        }}
                                                    >
                                                    <table className="table table-striped table-hover">
                                                        <thead style={{
                                                            position: "sticky",
                                                            top: 0,
                                                            background: "#fff",
                                                            zIndex: 2,
                                                        }}>
                                                            <tr>
                                                                <th>SKU / Número de parte</th>
                                                                <th>Detalle producto</th>
                                                                <th>Estado</th>
                                                                <th>Acción</th>
                                                            </tr>
                                                        </thead>

                                                        <tbody>
                                                            {loadingSkus ? (
                                                                <tr>
                                                                    <td colSpan="4" className="text-center py-4">
                                                                        <span className="text-muted">Cargando SKUs...</span>
                                                                    </td>
                                                                </tr>
                                                            ) : skus.length === 0 ? (
                                                                <tr>
                                                                    <td colSpan="4" className="text-center py-4">
                                                                        <span className="text-muted">
                                                                            Sin SKUs registrados para este desafío.
                                                                        </span>
                                                                    </td>
                                                                </tr>
                                                            ) : (
                                                                skus.map((sku) => (
                                                                    <tr key={sku.desafioSkuId}>
                                                                        <td>
                                                                            <strong>{sku.skuCodigo}</strong>
                                                                            <div className="text-muted small">
                                                                                {sku.skuCodigoNormalizado}
                                                                            </div>
                                                                        </td>

                                                                        <td title={sku.detalleProducto || ""}>
                                                                            {truncateText(sku.detalleProducto, 30)}
                                                                        </td>

                                                                        <td>
                                                                            {Number(sku.activo) === 1 ? (
                                                                                <span className="badge bg-success-subtle text-success p-2">Activo</span>
                                                                            ) : (
                                                                                <span className="badge bg-danger-subtle text-danger p-2">Inactivo</span>
                                                                            )}
                                                                        </td>

                                                                        <td>
                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-icon btn-warning-subtle"
                                                                                onClick={() => openEditSkuModal(sku)}
                                                                                title="Editar SKU"
                                                                            >
                                                                                <span className="btn-inner">
                                                                                    <svg
                                                                                        className="icon-24"
                                                                                        width="24"
                                                                                        viewBox="0 0 24 24"
                                                                                        fill="none"
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                    >
                                                                                        <path
                                                                                            opacity="0.4"
                                                                                            d="M19.9927 18.9534H14.2984C13.7429 18.9534 13.291 19.4124 13.291 19.9767C13.291 20.5422 13.7429 21.0001 14.2984 21.0001H19.9927C20.5483 21.0001 21.0001 20.5422 21.0001 19.9767C21.0001 19.4124 20.5483 18.9534 19.9927 18.9534Z"
                                                                                            fill="currentColor"
                                                                                        />
                                                                                        <path
                                                                                            d="M10.309 6.904L15.7049 11.2639C15.835 11.3682 15.8573 11.5596 15.7557 11.6929L9.35874 20.0282C8.95662 20.5431 8.36402 20.8344 7.72908 20.8452L4.23696 20.8882C4.05071 20.8903 3.88775 20.7613 3.84542 20.5764L3.05175 17.1266C2.91419 16.4926 3.05175 15.8358 3.45388 15.3306L9.88256 6.95675C9.98627 6.82132 10.1778 6.79743 10.309 6.904Z"
                                                                                            fill="currentColor"
                                                                                        />
                                                                                        <path
                                                                                            opacity="0.4"
                                                                                            d="M18.1208 8.66544L17.0806 9.96401C16.9758 10.0962 16.7874 10.117 16.6573 10.0127C15.3927 8.98901 12.1545 6.36285 11.2561 5.63509C11.1249 5.52744 11.1069 5.33607 11.2127 5.20275L12.2169 3.95706C13.1269 2.78534 14.7133 2.67768 15.9938 3.69923L17.4647 4.87095C18.0679 5.34377 18.47 5.967 18.6076 6.62278C18.7663 7.34405 18.597 8.05237 18.1208 8.66544Z"
                                                                                            fill="currentColor"
                                                                                        />
                                                                                    </svg>
                                                                                </span>
                                                                            </button>

                                                                            <button
                                                                                type="button"
                                                                                className="btn btn-icon btn-danger-subtle ms-2"
                                                                                onClick={() => handleDeleteSku(sku)}
                                                                                title="Desactivar SKU"
                                                                                disabled={Number(sku.activo) === 0}
                                                                            >
                                                                                <span className="btn-inner">
                                                                                    <svg
                                                                                        className="icon-24"
                                                                                        width="24"
                                                                                        viewBox="0 0 24 24"
                                                                                        fill="none"
                                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                                    >
                                                                                        <path
                                                                                            opacity="0.4"
                                                                                            d="M19.643 9.48851C19.643 9.5565 19.11 16.2973 18.8056 19.1342C18.615 20.8751 17.4927 21.9311 15.8092 21.9611C14.5157 21.9901 13.2494 22.0001 12.0036 22.0001C10.6809 22.0001 9.38741 21.9901 8.13185 21.9611C6.50477 21.9221 5.38147 20.8451 5.20057 19.1342C4.88741 16.2873 4.36418 9.5565 4.35445 9.48851C4.34473 9.28351 4.41086 9.08852 4.54507 8.93053C4.67734 8.78453 4.86796 8.69653 5.06831 8.69653H18.9388C19.1382 8.69653 19.3191 8.78453 19.4621 8.93053C19.5953 9.08852 19.6624 9.28351 19.643 9.48851Z"
                                                                                            fill="currentColor"
                                                                                        />
                                                                                        <path
                                                                                            d="M21 5.97686C21 5.56588 20.6761 5.24389 20.2871 5.24389H17.3714C16.7781 5.24389 16.2627 4.8219 16.1304 4.22692L15.967 3.49795C15.7385 2.61698 14.9498 2 14.0647 2H9.93624C9.0415 2 8.26054 2.61698 8.02323 3.54595L7.87054 4.22792C7.7373 4.8219 7.22185 5.24389 6.62957 5.24389H3.71385C3.32386 5.24389 3 5.56588 3 5.97686V6.35685C3 6.75783 3.32386 7.08982 3.71385 7.08982H20.2871C20.6761 7.08982 21 6.75783 21 6.35685V5.97686Z"
                                                                                            fill="currentColor"
                                                                                        />
                                                                                    </svg>
                                                                                </span>
                                                                            </button>
                                                                        </td>
                                                                    </tr>
                                                                ))
                                                            )}
                                                        </tbody>
                                                    </table>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* TAB REPORTE */}
                                            <div
                                                className="tab-pane fade"
                                                id="reporte-tab-pane"
                                                role="tabpanel"
                                                aria-labelledby="reporte-tab"
                                                tabIndex="0"
                                            >
                                                <div className="d-flex justify-content-between align-items-center mb-3">
                                                    <div>
                                                        <h5 className="mb-0">
                                                            Generar reporte
                                                        </h5>
                                                        <small className="text-muted">
                                                            Espacio reservado para reportes del desafío.
                                                        </small>
                                                    </div>
                                                </div>

                                                <div
                                                    className="border rounded d-flex align-items-center justify-content-center"
                                                    style={{
                                                        minHeight: "360px",
                                                        background: "#f8f9fa",
                                                    }}
                                                >
                                                    <div className="text-center">
                                                        <h5 className="text-muted">
                                                            Reporte pendiente por configurar
                                                        </h5>
                                                        <p className="text-muted mb-0">
                                                            Aquí se colocarán filtros, métricas y botones de exportación.
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            {/* MODAL IMPORTAR SKUS */}
            <div
                className="modal fade"
                id="importSkusModal"
                tabIndex="-1"
                aria-labelledby="importSkusModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-fullscreen">
                    <div className="modal-content">
                        <div className="modal-header">
                            <div>
                                <h5 className="modal-title" id="importSkusModalLabel">
                                    Importar SKUs del desafío
                                </h5>
                                <small className="text-muted">
                                    Valida el Excel antes de confirmar la importación.
                                </small>
                            </div>

                            <button
                                type="button"
                                className="btn-close"
                                data-bs-dismiss="modal"
                                aria-label="Close"
                                onClick={resetSkuImportModal}
                                disabled={skuImportLoading}
                            ></button>
                        </div>

                        <div className="modal-body">
                            {skuImportError && (
                                <div className="alert alert-danger d-flex justify-content-between align-items-center">
                                    <span>{skuImportError}</span>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setSkuImportError("")}
                                    ></button>
                                </div>
                            )}

                            <div className="row g-3 mb-4">
                                <div className="col-lg-4">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h6 className="mb-2">1. Formato requerido</h6>
                                            <p className="text-muted mb-2">
                                                El Excel debe contener estas columnas:
                                            </p>

                                            <ul className="mb-0">
                                                <li>Desafío al que pertenece</li>
                                                <li>
                                                    <strong>SKU</strong> <span className="text-danger">*</span>
                                                </li>
                                                <li>Detalle producto</li>
                                            </ul>

                                            <small className="text-muted d-block mt-2">
                                                Solo SKU es obligatorio.
                                            </small>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h6 className="mb-2">2. Subir archivo</h6>
                                            <p className="text-muted mb-3">
                                                Selecciona el Excel con los SKUs permitidos.
                                            </p>

                                            <input
                                                type="file"
                                                className="form-control form-control-sm"
                                                accept=".xlsx,.xls"
                                                onChange={handleSkuFileChange}
                                                disabled={skuImportLoading}
                                            />

                                            {selectedSkuFile && (
                                                <small className="text-success d-block mt-2">
                                                    Archivo seleccionado: {selectedSkuFile.name}
                                                </small>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="col-lg-4">
                                    <div className="card h-100">
                                        <div className="card-body">
                                            <h6 className="mb-2">3. Validar e importar</h6>
                                            <p className="text-muted mb-3">
                                                Primero genera el preview. Después confirma la importación.
                                            </p>

                                            <div className="d-flex gap-2 flex-wrap">
                                                <button
                                                    type="button"
                                                    className="btn btn-success btn-sm"
                                                    onClick={handlePreviewSkuExcel}
                                                    disabled={skuImportLoading || !selectedSkuFile}
                                                >
                                                    Validar Excel
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {skuPreview && (
                                <>
                                    <div className="row g-3 mb-4">
                                        <div className="col-md-2">
                                            <div className="card">
                                                <div className="card-body text-center">
                                                    <h4>{skuPreview.totalRows}</h4>
                                                    <small>Total filas</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="card">
                                                <div className="card-body text-center">
                                                    <h4 className="text-success">{skuPreview.validRows}</h4>
                                                    <small>Válidas</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="card">
                                                <div className="card-body text-center">
                                                    <h4 className="text-danger">{skuPreview.errorRows}</h4>
                                                    <small>Con error</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="card">
                                                <div className="card-body text-center">
                                                    <h4 className="text-warning">{skuPreview.duplicatedRows}</h4>
                                                    <small>Duplicados</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="card">
                                                <div className="card-body text-center">
                                                    <h4 className="text-primary">{skuPreview.uniqueSkus}</h4>
                                                    <small>SKUs únicos</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="col-md-2">
                                            <div className="card">
                                                <div className="card-body text-center">
                                                    <h4 className="text-secondary">{skuPreview.emptySkuRows}</h4>
                                                    <small>Sin SKU</small>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="card border mb-4">
                                        <div className="card-header d-flex justify-content-between align-items-center mb-4">
                                            <div>
                                                <h6 className="mb-0">Previsualización del archivo</h6>
                                                <small className="text-muted">
                                                    Los registros con error se omitirán en la importación.
                                                </small>
                                            </div>
                                            <div>
                                                <button
                                                    type="button"
                                                    className="btn btn-primary btn-sm"
                                                    onClick={handleImportSkus}
                                                    disabled={
                                                        skuImportLoading ||
                                                        !skuPreview ||
                                                        skuPreview.validRows <= 0
                                                    }
                                                >
                                                    {skuImportLoading ? "Importando..." : "Importar SKUs"}
                                                </button>
                                            </div>
                                        </div>

                                        <div className="card-body p-0">
                                            <div
                                                className="table-responsive"
                                                style={{
                                                    maxHeight: "420px",
                                                    overflowY: "auto",
                                                }}
                                            >
                                                <table className="table table-sm table-striped table-hover mb-0">
                                                    <thead
                                                        
                                                    >
                                                        <tr>
                                                            <th>Fila</th>
                                                            <th>Estado</th>
                                                            <th>Acción</th>
                                                            <th>Desafío al que pertenece</th>
                                                            <th>SKU</th>
                                                            <th>SKU normalizado</th>
                                                            <th>Detalle producto</th>
                                                            <th>Observaciones</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody>
                                                        {skuPreview.rows.map((row) => (
                                                            <tr key={row.rowNumber}>
                                                                <td>{row.rowNumber}</td>

                                                                <td>
                                                                    {row.estado === "Válida" ? (
                                                                        <span
                                                                            className="badge bg-success-subtle text-success p-2"
                                                                        >
                                                                            Válida
                                                                        </span>
                                                                    ) : (
                                                                        <span
                                                                            className="badge bg-danger-subtle text-danger p-2"
                                                                        >
                                                                            Error
                                                                        </span>
                                                                    )}
                                                                </td>

                                                                <td>
                                                                    {row.accion === "Crear" && (
                                                                        <span
                                                                            className="badge bg-primary-subtle text-primary p-2"
                                                                        >
                                                                            Crear
                                                                        </span>
                                                                    )}

                                                                    {row.accion === "Actualizar" && (
                                                                        <span
                                                                            className="badge bg-warning-subtle text-warning p-2"
                                                                        >
                                                                            Actualizar
                                                                        </span>
                                                                    )}

                                                                    {row.accion === "Error" && (
                                                                        <span
                                                                            className="badge bg-danger-subtle text-danger p-2"
                                                                        >
                                                                            Error
                                                                        </span>
                                                                    )}
                                                                </td>

                                                                <td>{row.desafio || "-"}</td>
                                                                <td>{row.sku || "-"}</td>
                                                                <td>{row.skuNormalizado || "-"}</td>
                                                                <td>{row.detalleProducto || "-"}</td>

                                                                <td>
                                                                    {row.errors.length ? row.errors.join(" | ") : "-"}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* {skuImportResult && (
                                <div className="card border border-success">
                                    <div className="card-header">
                                        <h6 className="mb-0">Resumen de importación</h6>
                                    </div>

                                    <div className="card-body">
                                        <div className="row g-3">
                                            <div className="col-md-2">
                                                <div className="text-center">
                                                    <h4>{skuImportResult.totalFilas}</h4>
                                                    <small>Total filas</small>
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="text-center">
                                                    <h4 className="text-success">
                                                        {skuImportResult.insertados}
                                                    </h4>
                                                    <small>Nuevos</small>
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="text-center">
                                                    <h4 className="text-warning">
                                                        {skuImportResult.reactivados}
                                                    </h4>
                                                    <small>Actualizados</small>
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="text-center">
                                                    <h4 className="text-primary">
                                                        {skuImportResult.yaAsignados}
                                                    </h4>
                                                    <small>Ya asignados</small>
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="text-center">
                                                    <h4 className="text-danger">
                                                        {(skuImportResult.filasSinSKU || 0) +
                                                            (skuImportResult.noEncontrados || 0)}
                                                    </h4>
                                                    <small>Errores</small>
                                                </div>
                                            </div>

                                            <div className="col-md-2">
                                                <div className="text-center">
                                                    <h4 className="text-secondary">
                                                        {skuImportResult.duplicadosEnArchivo}
                                                    </h4>
                                                    <small>Duplicados archivo</small>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="d-flex justify-content-end mt-4">
                                            <button
                                                type="button"
                                                className="btn btn-success"
                                                data-bs-dismiss="modal"
                                                onClick={resetSkuImportModal}
                                            >
                                                Finalizar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )} */}
                        </div>

                        <div className="modal-footer">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                data-bs-dismiss="modal"
                                onClick={resetSkuImportModal}
                                disabled={skuImportLoading}
                            >
                                Cerrar
                            </button>

                            {/* <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleImportSkus}
                                disabled={
                                    skuImportLoading ||
                                    !skuPreview ||
                                    skuPreview.validRows <= 0
                                }
                            >
                                {skuImportLoading ? "Importando..." : "Confirmar importación"}
                            </button> */}
                        </div>
                    </div>
                </div>
            </div>

            {/* MODAL CREAR / EDITAR SKU */}
            <div
                className="modal fade"
                id="skuCrudModal"
                tabIndex="-1"
                aria-labelledby="skuCrudModalLabel"
                aria-hidden="true"
            >
                <div className="modal-dialog modal-lg modal-dialog-centered">
                    <div className="modal-content">
                        <form onSubmit={handleSaveSku}>
                            <div className="modal-header">
                                <div>
                                    <h5 className="modal-title" id="skuCrudModalLabel">
                                        {skuFormMode === "edit" ? "Editar SKU" : "Agregar SKU"}
                                    </h5>

                                    <small className="text-muted">
                                        {skuFormMode === "edit"
                                            ? "Actualiza la información del SKU permitido."
                                            : "Agrega manualmente un SKU permitido para este desafío."}
                                    </small>
                                </div>

                                <button
                                    type="button"
                                    className="btn-close"
                                    data-bs-dismiss="modal"
                                    aria-label="Close"
                                    disabled={savingSku}
                                    onClick={resetSkuForm}
                                ></button>
                            </div>

                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-md-6">
                                        <label className="form-label">
                                            SKU / Número de parte <span className="text-danger">*</span>
                                        </label>

                                        <input
                                            type="text"
                                            className="form-control"
                                            value={skuForm.skuCodigo}
                                            onChange={(e) =>
                                                handleSkuFormChange("skuCodigo", e.target.value)
                                            }
                                            maxLength={120}
                                            disabled={savingSku}
                                            placeholder="Ej. ABC123"
                                        />

                                        <small className="text-muted">
                                            El sistema normalizará el SKU automáticamente.
                                        </small>
                                    </div>

                                    <div className="col-md-6">
                                        <label className="form-label">Estado</label>

                                        <select
                                            className="form-select"
                                            value={skuForm.activo}
                                            onChange={(e) =>
                                                handleSkuFormChange("activo", e.target.value)
                                            }
                                            disabled={savingSku}
                                        >
                                            <option value="1">Activo</option>
                                            <option value="0">Inactivo</option>
                                        </select>
                                    </div>

                                    <div className="col-md-12">
                                        <label className="form-label">Detalle producto</label>

                                        <textarea
                                            className="form-control"
                                            rows="3"
                                            value={skuForm.detalleProducto}
                                            onChange={(e) =>
                                                handleSkuFormChange(
                                                    "detalleProducto",
                                                    e.target.value
                                                )
                                            }
                                            maxLength={255}
                                            disabled={savingSku}
                                            placeholder="Detalle o descripción del producto"
                                        />

                                        <small className="text-muted">
                                            Máximo 255 caracteres.
                                        </small>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-footer">
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    data-bs-dismiss="modal"
                                    disabled={savingSku}
                                    onClick={resetSkuForm}
                                >
                                    Cancelar
                                </button>

                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={savingSku}
                                >
                                    {savingSku
                                        ? "Guardando..."
                                        : skuFormMode === "edit"
                                            ? "Actualizar SKU"
                                            : "Guardar SKU"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
}

export default ChampionsDetail;